import { assign } from 'lodash-es';
import { Rfp_Mod } from './rfps/rfp.service';
import {
  Tier,
  Characteristic,
  Spend,
  Seller,
  Unspsc,
  Term,
  Record,
} from './api.interfaces';
import { RfpPermissionResult } from '../../../../../../../../frontend/libs/shared/src';
import { Observable } from 'rxjs';

export interface MicroChart {
  last12MonthsSpend: number;
  maxYAxisRange: number;
  seller: {
    id: number;
    name: string;
  };
  sellerCoverage: number;
  sellerMaxYAxisRange: number;
  tierType: string;
  tiers: MicroChartTier[];
}

interface MicroChartTier {
  maxPct: number;
  min: number;
  minNumberOfConversions: number;
  minPct: number;
  minSpend: number;
  order: number;
  threshold: number;
  tier: {
    id: number;
    name: string;
  };
}

interface OfferCpa {
  commitmentType: string;
  terminationDate: string;
  measured: boolean;
  contracted: boolean;
  term: Term;
  id: number;
  publishedId: string;
  effectiveDate: string;
  expirationDate: string;
  status: any;
}

export interface Offer {
  amendmentDate: string;
  catalogChanges: {
    changesLastAutoAppliedOn: boolean;
    autoAppliedChanges: boolean;
    records: Record[];
  };
  contractActivationMinLeadTime: any;
  cpa: OfferCpa;
  cpaId: number;
  createdDate: string;
  createdBy: string;
  currentMarketShare: number;
  description: string;
  durations: string;
  id: number;
  prices?: Array<any>;
  isStructureComplete?: any;
  lastModifiedBy: string;
  lastModifiedDate: string;
  marketShareTier?: any;
  model;
  name: string;
  numberOfPricedProducts: number;
  numberOfUnPricedProducts: number;
  pricingLoaded?: boolean;
  pricingTypes: string;
  productPricingStatus: any;
  rebateProxyAuthorization: boolean;
  rfp: Rfp_Mod;
  rfpId: number;
  rfpRecipientId: number;
  seller: Seller;
  sellerId: number;
  spendLevelType: {
    name: string;
    displayName: string;
    fieldNames: string[];
    operator?: any;
  };
  status: any;
  supportedTerms: Term;
  term: Term;
  tiers: Tier[];
  totalNumberOfProducts: number;
  whenSent: string;
  selectedTier: unknown;
  unorderedPricing?: boolean;
  unorderedTiers?: boolean;
  submittedBy?: string;
}

export default class OffersService {
  static $inject = ['$http'];

  baseUrl = '/aptitude/api/offers';

  constructor(private $http) {
    'ngInject';
  }

  getPricing(offerId: number): ng.IPromise<any> {
    return this.$http
      .get(`${this.baseUrl}/${offerId}/products`)
      .then((products) => {
        // Lol what? Is this the best way of doing this?
        // Why don't we just pass the fields we care about?
        return products.map((product) => {
          delete product.catalogNumberNorm;
          delete product.number;
          delete product.pimKey;
          delete product.parentPimKey;
          delete product.status;
          delete product.categoryIdentifier;
          delete product.manufacturerName;
          delete product.manufacturerId;
          delete product.startDate;
          delete product.endDate;
          delete product.createdBy;
          delete product.createdDate;
          delete product.lastModifiedBy;
          delete product.lastModifiedDate;
          delete product.lastModifiedById;
          delete product.createdById;
          delete product.onCA;
          delete product.mergedProductId;
          delete product.vizientContract;
          delete product.spend;
          delete product.isPimKeyInProductMaster;
          delete product.source;
          delete product.aptitudeProductId;
          delete product.packageName;
          delete product.crossReferenceCount;
          delete product.crossApproveCount;
          delete product.crossDisputeCount;
          delete product.unitsPurchased;
          delete product.historicalMinPrice;
          delete product.historicalMaxPrice;
          delete product.historicalMedianPrice;
          delete product.manufacturer;
          delete product.seller;
          delete product.supplier;
          delete product.parentSupplier;
          delete product.smartKey;
          delete product.unspscString;
          delete product.inactive;

          return product;
        });
      });
  }

  getPricingStatus(offerId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${offerId}/productpricingstatus`);
  }

  /**
   * Get the microchart data for an offer
   *
   * @param  {number}                  offerId                   The offerId
   * @param  {number}                  partnerId                 Only necessary for partner viewing a group rfp
   * @param  {boolean}                 usePrimaryCrossReferences [description]
   * @return {ng.IPromise<MicroChart>}                           [description]
   */
  getMicrochart(
    offerId: number,
    partnerId?: number,
    usePrimaryCrossReferences?: boolean,
    configOverrides = {},
  ): ng.IPromise<MicroChart> {
    return this.$http.get(
      `${this.baseUrl}/${offerId}/microchart`,
      assign({}, configOverrides, {
        params: {
          // offerId,
          partnerId,
          usePrimaryCrossReferences,
        },
      }),
    );
  }

  delete(offerId: number): ng.IPromise<any> {
    return this.$http.delete(`/aptitude/api/offers/${offerId}`, null);
  }

  getRfpPermissions(rfpId: number): ng.IPromise<any> {
    return this.$http.post(`/apt-service/rfp/graphql`, {
      query: `query getRfpPermissions($rfpId: Long!) {
        permissions(contextId: $rfpId) {
          hasAccess
          reasonForDenial
          permissionType
        }
     }`,
      operationName: 'permissions',
      variables: { rfpId: rfpId },
    });
  }

  clone(offerId: number): ng.IPromise<any> {
    return this.$http.post(`/apt-service/offer/graphql`, {
      query: `mutation CloneOffer($offerId: Long!) {
       cloneOffer(cloneOfferRequest: { offerId: $offerId }) {
         id
         name
         auditInfo {
           createdBy
           createdDate
           lastModifiedBy
           lastModifiedDate
         }
         tiers {
           id
           name
           description
           order
           tierOrderDisplay
           dollarMarketShare
           unitMarketShare
           absoluteDollars
           absoluteUnits
           spendLevelType
           removable
           tierType
         }
       }
     }`,
      operationName: 'cloneOffer',
      variables: { offerId: offerId },
    });
  }

  getOfferPricingDownloadUrl(offerId: number): Observable<any> {
    return new Observable((observer) => {
      this.$http
        .post('/apt-service/offer/graphql', {
          query: `query getOfferPricingDownload($id: Long) {
                  offerPricingDownload(id: $id) {
                    url
                  }
                }`,
          operationName: 'getOfferPricingDownload',
          variables: { id: offerId },
        })
        .then((response) => {
          observer.next(response.data);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  getPricingFields(offerId: number): ng.IPromise<any> {
    return this.$http.get(`/aptitude/api/offers/${offerId}/pricingfields`);
  }

  importPrices(offerId: number, uploadId): ng.IPromise<any> {
    return this.$http.put(`/aptitude/api/offers/${offerId}/prices`, {
      fileUploadRequestId: uploadId,
    });
  }

  update(offerId: number, fields) {
    return this.$http.put(`${this.baseUrl}/${offerId}`, fields);
  }

  updateProductsPrices(offerId: number, changeList: any[]): ng.IPromise<any> {
    // Add this update to a queue so we will perform the updates one at a time rather than all at once.
    return this.$http.put(`${this.baseUrl}/${offerId}/products`, changeList);
  }

  updateProductPackage(
    offerId: number,
    productId: number,
    uom: string,
    unitsInPackage: number,
  ) {
    let changeList = [
      {
        p: productId,
        uom,
        uc: unitsInPackage,
      },
    ];

    return this.$http.put(
      `${this.baseUrl}/${offerId}/products/compressed`,
      changeList,
    );
  }

  updateProductsPricesCompressed(
    offerId: number,
    changeList: any[],
  ): ng.IPromise<any> {
    // Add this update to a queue so we will perform the updates one at a time rather than all at once.
    return this.$http.put(
      `${this.baseUrl}/${offerId}/products/compressed`,
      changeList,
    );
  }

  modalUpdateTiers(offerId: number, tiers: any[]): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${offerId}/tiers`, tiers);
  }

  updateTiersOld(offerId: number, tiers: any[]): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${offerId}/updatetiers`, tiers);
  }

  updateTiers(
    offerId: number,
    tiers: any[],
    spendLevelType: any,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${offerId}/updatetiers`, {
      tiers: tiers,
      spendLevelTypeName: spendLevelType.name,
    });
  }

  insertTiers(offerId: number, tiers: any[]): ng.IPromise<any> {
    return this.$http.post(`${this.baseUrl}/${offerId}/tiers`, tiers);
  }

  removeTier(offerId: number, tierId: number): ng.IPromise<any> {
    return this.$http.delete(`${this.baseUrl}/${offerId}/tiers/${tierId}`);
  }

  // Gets attached upload files - in use @ offerPricing page
  getAttachments(offerId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${offerId}/documents`);
  }

  // @param suppressAction - prevent the interceptor from capturing the HTTP error and allow caller to handle it
  // Uploads selected attachments - in use @ offerPricing page
  uploadAttachments(
    offerId: number,
    Upload,
    files: any[],
    suppressAction = false,
  ): ng.IPromise<any> {
    return Upload.upload({
      url: `${this.baseUrl}/${offerId}/documents`,
      arrayKey: '',
      data: {
        files,
      },
      suppressAction,
    });
  }

  // Delete uploaded attachments - in use @ offerPricing page
  // @suppressAction: false - prevent interceptor from handling HTTP error - in multi-file upload components generally pass true
  deleteAttachment(
    offerId: number,
    fileId: string,
    suppressAction,
  ): ng.IPromise<any> {
    return this.$http.delete(`${this.baseUrl}/${offerId}/documents/${fileId}`, {
      suppressAction,
    });
  }
}
